import React, { useState } from 'react';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonTitle,
  IonToolbar,
  IonBadge,
  IonChip,
  IonText,
  IonSegment,
  IonSegmentButton,
  IonToast,
  useIonToast,
} from '@ionic/react';
import {
  notificationsOutline,
  checkmarkCircleOutline,
  alertCircleOutline,
  informationCircleOutline,
  warningOutline,
  closeOutline,
} from 'ionicons/icons';
import { useNotifications } from '../context/NotificationContext';
import { useLanguage } from '../context/LanguageContext';
import { formatDateTime } from '../utils/format';

const Notifications: React.FC = () => {
  const { t } = useLanguage();
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    removeNotification
  } = useNotifications();
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  const filteredNotifications = notifications.filter(notif =>
    filter === 'unread' ? !notif.read : true
  );

  const handleNotificationClick = (notif: any) => {
    if (!notif.read) {
      markAsRead(notif.id);
    }
    if (notif.onAction) {
      notif.onAction();
    }
    if (notif.actionUrl) {
      window.location.href = notif.actionUrl;
    }
    setShowModal(false);
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return checkmarkCircleOutline;
      case 'error':
        return alertCircleOutline;
      case 'warning':
        return warningOutline;
      default:
        return informationCircleOutline;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'success':
        return 'success';
      case 'error':
        return 'danger';
      case 'warning':
        return 'warning';
      default:
        return 'medium';
    }
  };

  return (
    <>
      <IonButton
        fill="clear"
        onClick={() => setShowModal(true)}
        style={{ position: 'relative' }}
      >
        <IonIcon icon={notificationsOutline} style={{ fontSize: '24px' }} />
        {unreadCount > 0 && (
          <IonBadge
            color="danger"
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              fontSize: '10px',
              minWidth: '16px',
              height: '16px',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </IonBadge>
        )}
      </IonButton>

      <IonModal isOpen={showModal} onDidDismiss={() => setShowModal(false)}>
        <IonPage>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{t('notifications') || 'Notifications'}</IonTitle>
              <IonButtons slot="start">
                <IonButton onClick={() => setShowModal(false)}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
              <IonButtons slot="end">
                {unreadCount > 0 && (
                  <IonButton onClick={markAllAsRead} fill="clear">
                    {t('markAllAsRead') || 'Mark all as read'}
                  </IonButton>
                )}
              </IonButtons>
            </IonToolbar>
            <IonToolbar>
              <IonSegment
                value={filter}
                onIonChange={(e) => setFilter(e.detail.value as 'all' | 'unread')}
              >
                <IonSegmentButton value="all">
                  <IonLabel>{t('all') || 'All'}</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="unread">
                  <IonLabel>{t('unread') || 'Unread'}</IonLabel>
                  {unreadCount > 0 && (
                    <IonBadge color="danger" style={{ marginLeft: '4px' }}>
                      {unreadCount}
                    </IonBadge>
                  )}
                </IonSegmentButton>
              </IonSegment>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            {filteredNotifications.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  color: 'var(--ion-color-medium)',
                }}
              >
                <IonIcon
                  icon={notificationsOutline}
                  style={{ fontSize: '48px', marginBottom: '16px' }}
                />
                <IonText>
                  {filter === 'unread'
                    ? (t('noUnreadNotifications') || 'No unread notifications')
                    : (t('noNotifications') || 'No notifications')}
                </IonText>
              </div>
            ) : (
              <IonList>
                {filteredNotifications.map((notif) => (
                  <IonItem
                    key={notif.id}
                    button
                    onClick={() => handleNotificationClick(notif)}
                    style={{
                      backgroundColor: notif.read ? 'transparent' : 'var(--ion-color-light)',
                    }}
                  >
                    <IonIcon
                      slot="start"
                      icon={getNotificationIcon(notif.type)}
                      color={getNotificationColor(notif.type)}
                      style={{ marginRight: '16px' }}
                    />
                    <IonLabel>
                      <h2
                        style={{
                          fontWeight: notif.read ? 'normal' : 'bold',
                          color: notif.read ? 'var(--ion-color-medium)' : 'var(--ion-color-dark)',
                        }}
                      >
                        {notif.title}
                      </h2>
                      <p
                        style={{
                          color: notif.read ? 'var(--ion-color-medium)' : 'var(--ion-color-dark)',
                        }}
                      >
                        {notif.message}
                      </p>
                      <IonText
                        style={{
                          fontSize: '12px',
                          color: 'var(--ion-color-medium)',
                        }}
                      >
                        {formatDateTime(new Date(notif.timestamp))}
                      </IonText>
                      {notif.actionLabel && (
                        <IonChip
                          color="primary"
                          style={{ marginTop: '8px' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNotificationClick(notif);
                          }}
                        >
                          {notif.actionLabel}
                        </IonChip>
                      )}
                    </IonLabel>
                    <IonButton
                      slot="end"
                      fill="clear"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeNotification(notif.id);
                      }}
                    >
                      <IonIcon icon={closeOutline} />
                    </IonButton>
                  </IonItem>
                ))}
              </IonList>
            )}
          </IonContent>
        </IonPage>
      </IonModal>
    </>
  );
};

export default Notifications;